import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Truck, Plus, Trash2, Lock, ArrowLeft } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { NurseryDeliveryZone, NurseryCustomerTier, CreateDeliveryZonePayload } from '../../types/nursery';

const MATCH_TYPES: { value: NurseryDeliveryZone['match_type']; label: string; hint: string }[] = [
  { value: 'all', label: 'All postcodes', hint: 'Catch-all rate for anywhere' },
  { value: 'postcode_prefix', label: 'Postcode prefix', hint: 'e.g. 30,32 matches 3000–3299' },
  { value: 'postcode_list', label: 'Postcode list', hint: 'e.g. 3000,3121,3142' },
];

const EMPTY_FORM: CreateDeliveryZonePayload = {
  name: '',
  match_type: 'all',
  match_value: '',
  flat_fee: 0,
  free_over_subtotal: null,
  customer_tier_id: null,
};

function money(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

function DeliveryZonesList() {
  const navigate = useNavigate();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateDeliveryZonePayload>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [zoneRes, tierRes] = await Promise.all([
        nurseryService.getDeliveryZones(true),
        nurseryService.getCustomerTiers(),
      ]);
      if (zoneRes.success) setZones(zoneRes.data || []);
      if (tierRes.success) setTiers(tierRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load delivery zones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addZone = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await nurseryService.createDeliveryZone({
        ...form,
        match_value: form.match_type === 'all' ? null : (form.match_value || '').trim() || null,
        free_over_subtotal: form.free_over_subtotal ?? null,
      });
      if (res.success) {
        setForm(EMPTY_FORM);
        await load();
      } else {
        setError((res as { error?: { message?: string } }).error?.message || 'Failed to save');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeZone = async (id: number) => {
    await nurseryService.deleteDeliveryZone(id);
    setZones((z) => z.filter((x) => x.id !== id));
  };

  const toggleActive = async (zone: NurseryDeliveryZone) => {
    const res = await nurseryService.updateDeliveryZone(zone.id, { active: !zone.active });
    if (res.success) setZones((z) => z.map((x) => (x.id === zone.id ? res.data : x)));
  };

  if (licenseLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Manager license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Configuring delivery zones requires an active <span className="font-medium">Nursery Manager</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to nursery
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Delivery Zones"
        subtitle="Pickup is always free. Set flat or postcode-zone delivery rates, optionally per tier."
        icon={<Truck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {/* Add form */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">New zone</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Metro Melbourne"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Applies to tier</label>
              <select
                value={form.customer_tier_id ?? ''}
                onChange={(e) => setForm({ ...form, customer_tier_id: e.target.value === '' ? null : Number(e.target.value) })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All tiers</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Match</label>
              <select
                value={form.match_type}
                onChange={(e) => setForm({ ...form, match_type: e.target.value as NurseryDeliveryZone['match_type'] })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {MATCH_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {form.match_type !== 'all' && (
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-neutral-500 mb-1">
                  Postcodes {form.match_type === 'postcode_prefix' ? '(prefixes)' : '(exact)'}
                </label>
                <input
                  value={form.match_value ?? ''}
                  onChange={(e) => setForm({ ...form, match_value: e.target.value })}
                  placeholder={MATCH_TYPES.find((m) => m.value === form.match_type)?.hint}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Flat fee (AUD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.flat_fee ?? 0}
                onChange={(e) => setForm({ ...form, flat_fee: Number(e.target.value) })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Free over (AUD, optional)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.free_over_subtotal ?? ''}
                onChange={(e) => setForm({ ...form, free_over_subtotal: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="Never free"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-end">
              <Button variant="primary" size="sm" onClick={addZone} disabled={saving || !form.name.trim()}>
                <Plus className="h-4 w-4" /> Add zone
              </Button>
            </div>
          </div>
        </div>

        {/* Zones list */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : zones.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Truck className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No delivery zones yet</p>
            <p className="text-sm mt-1">Without a matching zone, delivery is unavailable and customers must pick up.</p>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {zones.map((z) => (
              <div key={z.id} className={`flex items-center gap-4 px-4 py-3 ${z.active ? '' : 'opacity-50'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-800">{z.name}</span>
                    {z.tier_name && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {z.tier_name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {z.match_type === 'all' ? 'All postcodes' : `${z.match_type === 'postcode_prefix' ? 'Prefix' : 'List'}: ${z.match_value}`}
                    {' · '}{money(Number(z.flat_fee))}
                    {z.free_over_subtotal != null ? ` · free over ${money(Number(z.free_over_subtotal))}` : ''}
                  </p>
                </div>
                <button onClick={() => toggleActive(z)} className="text-xs text-neutral-500 hover:text-primary-600">
                  {z.active ? 'Disable' : 'Enable'}
                </button>
                <button onClick={() => removeZone(z.id)} className="text-neutral-400 hover:text-red-600 p-1" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default DeliveryZonesList;
